import React from "react";
import { Box, Tooltip, useTheme } from "@mui/material";
import { getPlatformColor, getPlatformOnColor, getPlatformShortLabel } from "../../config/theme";

interface PlatformChipProps {
  /** Provider label ("Underdog" | "DraftKings" | "Unknown"). */
  provider: string;
  /** sm = compact table cell / list row, md = default. */
  size?: "sm" | "md";
  /** Outlined reads quieter next to a colored CLV number; solid for emphasis. */
  variant?: "solid" | "outlined";
}

/**
 * Platform badge ("UD" / "DK"). Underdog and DraftKings are separate markets
 * with separate boards, so anywhere a number could belong to either one, this
 * chip says which. Colors come from the PLATFORM tokens (Underdog yellow,
 * DraftKings green), paired with the ink that stays legible on them — white on
 * the light-theme fills, near-black on the bright dark-theme ones.
 */
export default function PlatformChip({ provider, size = "md", variant = "solid" }: PlatformChipProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const color = getPlatformColor(provider, isDark);
  // White is NOT safe here: on the dark-theme yellow it lands at 1.5:1.
  const onColor = getPlatformOnColor(provider, isDark);
  const height = size === "sm" ? 18 : 22;
  const solid = variant === "solid";
  return (
    <Tooltip title={provider} arrow>
      <Box
        component="span"
        aria-label={provider}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          height,
          px: size === "sm" ? 0.625 : 0.875,
          bgcolor: solid ? color : "transparent",
          color: solid ? onColor : color,
          boxShadow: solid ? "none" : `inset 0 0 0 1px ${color}`,
          fontWeight: 700,
          fontSize: size === "sm" ? 9.5 : 11,
          lineHeight: 1,
          borderRadius: "3px",
          letterSpacing: "0.04em",
          flexShrink: 0,
        }}
      >
        {getPlatformShortLabel(provider)}
      </Box>
    </Tooltip>
  );
}
